import { useMemo } from 'react';
import { cloneDeep } from 'lodash';

import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

import { ReactComponent as AddIcon } from 'assets/images/icons/btn_add.svg';
import { ReactComponent as AddIconDisabled } from 'assets/images/icons/btn_add_disabled.svg';

// Components
import { SolidButton } from 'components/buttons/SolidButton';
import { OutlineButton } from 'components/buttons/OutlineButton';
import { OutlineRectangleButton } from 'components/buttons/OutlineRectangleButton';
import ModalContainer from 'components/ModalContainer';
import AddTable from './AddTable';

import { voiceVlanConfig, MAX_OUI_BINDING_ENTRIES } from '../const';

const validateMacAddress = macAddress => {
  const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
  return macRegex.test(macAddress);
};

const AddModal = ({ openAddModal, setOpenAddModal, formData, setFormData, showEditGeneral }) => {
  const theme = useTheme();

  const inputHasError = useMemo(() => {
    return formData?.addOuiList?.some(item => {
      const hasEmptyField = formData?.addOuiList?.some(item => item.address === '' || item.mask === '' || item.mask === '-----');
      const isInvalidMac = !validateMacAddress(item.address) || !validateMacAddress(item.mask);
      const isAddressDuplicate = formData?.addOuiList?.filter(i => i.address === item.address).length > 1;
      // const isMaskDuplicate = formData?.addOuiList?.filter(i => i.mask === item.mask).length > 1;
      // const isDuplicate = isAddressDuplicate || isMaskDuplicate;
      return hasEmptyField || isInvalidMac || isAddressDuplicate;
    });
  }, [formData?.addOuiList]);

  const checkAddBtnDisabled = () => {
    return (
      !showEditGeneral ||
      formData?.voiceVlanId < 2 ||
      formData?.voiceVlanId > 4094 ||
      formData?.ouiBindingList?.length + formData?.addOuiList?.length > MAX_OUI_BINDING_ENTRIES ||
      inputHasError
    );
  };

  const handleAddOuiItem = () => {
    if ((formData?.addOuiList?.length || 0) >= MAX_OUI_BINDING_ENTRIES) return;

    const maxItemId = formData?.addOuiList?.reduce((maxId, item) => (item.id > maxId ? item.id : maxId), 0);
    setFormData(prev => ({
      ...prev,
      addOuiList: [...(prev.addOuiList || []), { id: maxItemId + 1, address: '', mask: '', description: '', checked: false }],
    }));
  };

  const handleClickAddBtn = () => {
    // Handle the logic for adding the selected OUIs to the main list
    setFormData(prev => ({
      ...prev,
      ouiBindingList: [...(prev.ouiBindingList || []), ...prev?.addOuiList],
      addOuiList: cloneDeep(voiceVlanConfig.addOuiList).map(item => ({ ...item, checked: false })), // Reset the add list
    }));
    setOpenAddModal(false); // Close the modal after adding
  };

  const handleClickCancelBtn = () => {
    // Reset the addOuiList and close the modal
    setFormData(prev => ({
      ...prev,
      addOuiList: cloneDeep(voiceVlanConfig.addOuiList).map(item => ({ ...item, checked: false })), // Reset the add list
    }));
    setOpenAddModal(false);
  };

  return (
    <ModalContainer display={openAddModal}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '0 0 10px 20px',
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
          }}
        >
          Add OUIs
        </Typography>
        <Button
          onClick={handleClickCancelBtn}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>
      <Box sx={{ overflow: 'scroll' }}>
        {/* Table */}
        <AddTable
          {...{
            formData,
            setFormData,
            showEditGeneral,
          }}
        />
        {/* Add button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <OutlineRectangleButton
            startIcon={
              !(
                !showEditGeneral ||
                formData?.voiceVlanId < 2 ||
                formData?.voiceVlanId > 4094 ||
                formData?.ouiBindingList?.length + formData?.addOuiList?.length >= MAX_OUI_BINDING_ENTRIES
              ) ? (
                <AddIcon />
              ) : (
                <AddIconDisabled />
              )
            }
            height="30px"
            onClick={handleAddOuiItem}
            disabled={
              !showEditGeneral ||
              formData?.voiceVlanId < 2 ||
              formData?.voiceVlanId > 4094 ||
              formData?.ouiBindingList?.length + formData?.addOuiList?.length >= MAX_OUI_BINDING_ENTRIES
            }
          >
            Add New OUI Address
          </OutlineRectangleButton>
        </Box>
      </Box>

      <Box
        sx={{
          marginTop: '50px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <OutlineButton sx={{ marginRight: '20px' }} width="100px" height="32px" onClick={handleClickCancelBtn}>
          Cancel
        </OutlineButton>
        <SolidButton width="100px" height="32px" onClick={handleClickAddBtn} disabled={checkAddBtnDisabled()}>
          Add
        </SolidButton>
      </Box>
    </ModalContainer>
  );
};

export default AddModal;
